import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Drawer,
  Chip,
  Button
} from '@mui/material';
import { FORD_COLORS, FORD_TYPOGRAPHY } from '../../theme/fordTheme.js';

// Common style constants
const COMMON_STYLES = {
  border: '1px solid #233058',
  borderRadius: '8px',
  borderRadiusLarge: '12px',
  borderRadiusSmall: '6px',
  padding: '16px',
  paddingLarge: '20px',
  paddingSmall: '12px',
  fontSize: '0.875rem',
  fontSizeSmall: '0.75rem',
  transition: 'all 0.2s ease',
  hoverBackground: 'rgba(79, 124, 255, 0.1)',
  hoverBorderColor: '#4f7cff',
};

// Common chip color configurations
const CHIP_COLORS = {
  'chip-ok': {
    backgroundColor: 'rgba(34, 197, 94, 0.2)',
    color: '#22c55e',
    border: '1px solid rgba(34, 197, 94, 0.3)',
  },
  'chip-warn': {
    backgroundColor: 'rgba(251, 191, 36, 0.2)',
    color: '#fbbf24',
    border: '1px solid rgba(251, 191, 36, 0.3)',
  },
  'chip-error': {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    color: '#ef4444',
    border: '1px solid rgba(239, 68, 68, 0.3)',
  },
  default: {
    backgroundColor: 'rgba(156, 163, 199, 0.2)',
    color: '#9aa3c7',
    border: '1px solid rgba(156, 163, 199, 0.3)',
  },
};

// Common button styles
const BUTTON_BASE_STYLES = {
  backgroundColor: 'transparent',
  border: COMMON_STYLES.border,
  borderRadius: COMMON_STYLES.borderRadiusSmall,
  padding: '6px 12px',
  fontSize: COMMON_STYLES.fontSize,
  cursor: 'pointer',
  transition: COMMON_STYLES.transition,
  '&:hover': {
    backgroundColor: COMMON_STYLES.hoverBackground,
    borderColor: COMMON_STYLES.hoverBorderColor,
  },
  '&:disabled': {
    backgroundColor: 'transparent',
    color: '#9aa3c7',
    borderColor: '#233058',
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

// Main drawer component
export const OfflineEventsDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    width: '675px',
    maxWidth: '90vw',
    backgroundColor: FORD_COLORS.backgroundford,
    color: FORD_COLORS.textMuted,
    border: COMMON_STYLES.border,
    borderRadius: COMMON_STYLES.borderRadiusSmall,
  },
}));

// Drawer header
export const OfflineEventsDrawerHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start', // Changed to align text and button properly
  padding: '20px 24px', // Increased padding for better spacing
  backgroundColor: 'rgb(10, 37, 64)', // Dark navy blue background matching image
  color: '#ffffff',
  borderBottom: 'none', // Remove border for clean look
  minHeight: '80px', // Ensure adequate height for two-line layout
  '& h3': {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#ffffff',
  },
  '& .MuiButton-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)', // Semi-transparent button background
    color: '#ffffff',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '6px',
    padding: '8px 16px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  '& .MuiIconButton-root': {
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
}));

// Drawer header title styling
export const OfflineEventsDrawerTitle = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: 'rgba(255, 255, 255, 0.7)',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  lineHeight: 1.2,
  display: 'block'
}));

// Drawer header device name styling
export const OfflineEventsDrawerDeviceName = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 700,
  color: '#ffffff',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  lineHeight: 1.2,
  marginTop: '4px'
}));

// Drawer content area
export const OfflineEventsDrawerContent = styled(Box)(({ theme }) => ({
  padding: COMMON_STYLES.paddingLarge,
  height: '100%',
  overflowY: 'auto',
  backgroundColor: FORD_COLORS.backgroundford,
}));

// Meta information section
export const OfflineEventsMetaSection = styled(Box)(({ theme }) => ({
 display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 2fr))', // Increased minWidth for better spacing
  padding: COMMON_STYLES.padding,
  borderRadius: COMMON_STYLES.borderRadiusLarge,
  marginBottom: COMMON_STYLES.paddingLarge,
  border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
  backgroundColor: '#ffffff', // Ensure white background
  '& > div': {
    display: 'flex',
    flexDirection: 'column',
    // gap: '1px',
    paddingTop: '9px', // Add some vertical padding
    '& b': {
      textTransform: 'uppercase',
      fontWeight: 700,
      color: 'rgb(138, 152, 166)',
      fontSize: '12px',
      letterSpacing: '0.5px',
      lineHeight: 1.2,
      marginBottom: '2px',
    },
    '& span': {
      color: 'rgb(19, 34, 52)',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: 1.3,
    },
  },
}));

// Device detail row styling
export const DeviceDetailRowContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  padding: '1px 0', // Add some vertical padding
  fontFamily: FORD_TYPOGRAPHY.newfont,
  '& .detail-label': {
    textTransform: 'uppercase',
    fontWeight: 700,
    color: 'rgb(138, 152, 166)',
    fontSize: '12px',
    letterSpacing: '0.5px',
    lineHeight: 1.2,
    marginBottom: '2px',
  },
  '& .detail-value': {
    color: 'rgb(19, 34, 52)',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.3,
  },
}));

// Table wrapper
export const OfflineEventsTableWrapper = styled(Box)(({ theme }) => ({
  overflowX: 'auto',
  overflowY: 'auto',
  position: 'relative',
  border: COMMON_STYLES.border,
  // borderRadius: COMMON_STYLES.borderRadius,
  marginBottom: COMMON_STYLES.paddingLarge,
  // Hide scrollbar while maintaining scroll functionality
  scrollbarWidth: 'none', // Firefox
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari, Edge
  },
  msOverflowStyle: 'none', // Internet Explorer 10+
  '& table': {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: '12px',
    '& thead': {
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
    '& th': {
      backgroundColor: 'rgb(10, 37, 64)', // Dark navy blue background to match OutageTable
      color: '#ffffff', // White text
      fontWeight: 700, // Bolder font weight
      textTransform: 'uppercase', // Capital letters
      letterSpacing: '0.5px', // Better readability
      padding: `${COMMON_STYLES.paddingSmall} ${COMMON_STYLES.padding}`,
      textAlign: 'left',
      borderBottom: 'none', // Remove border to match OutageTable
      position: 'sticky',
      top: 0,
      zIndex: 11,
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
      borderRadius: 0,
      // Column-specific widths
      '&:nth-of-type(1)': { width: '30%' }, // Offline Time
      '&:nth-of-type(2)': { width: '30%' }, // Online Time
      '&:nth-of-type(3)': { width: '15%' }, // Status (smaller)
      '&:nth-of-type(4)': { width: '25%' }, // Duration (larger for more text)
    },
    '& tbody': {
      position: 'relative',
      zIndex: 1,
    },
    '& td': {
      padding: `8px`,
      borderBottom: '1px solid rgba(35, 48, 88, 0.5)',
      color: 'rgb(19, 34, 52)',
      backgroundColor: FORD_COLORS.backgroundford,
      // Match header column widths
      '&:nth-of-type(1)': { width: '30%' }, // Offline Time
      '&:nth-of-type(2)': { width: '30%' }, // Online Time
      '&:nth-of-type(3)': { width: '15%' }, // Status (smaller)
      '&:nth-of-type(4)': { width: '25%' }, // Duration (larger for more text)
    },
    '& tr:hover td': {
      backgroundColor: COMMON_STYLES.hoverBackground,
    },
  },
}));

// Status chip styling
export const OfflineEventsStatusChip = styled(Chip)(({ theme, chiptype }) => {
  return {
    ...(CHIP_COLORS[chiptype] || CHIP_COLORS.default),
    fontSize: COMMON_STYLES.fontSizeSmall,
    height: '24px',
    borderRadius: COMMON_STYLES.borderRadiusLarge,
    fontWeight: '500',
  };
});

// Loading message
export const OfflineEventsLoadingMessage = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '10px',
  fontSize: '1rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '16px',
  color: FORD_COLORS.textMuted,
  fontFamily: FORD_TYPOGRAPHY.newfont,
  borderRadius: COMMON_STYLES.borderRadiusLarge,
  marginBottom: '10px',
  border: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1)',
}));

// Spinner
export const OfflineEventsSpinner = styled(Box)(({ theme }) => ({
  width: '20px',
  height: '20px',
  border: '2px solid #233058',
  borderTop: '2px solid #4f7cff',
  borderRadius: '50%',
  animation: 'spin 1s linear infinite',
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
}));

// Error box
export const OfflineEventsErrorBox = styled(Box)(({ theme }) => ({
  marginBottom: COMMON_STYLES.padding,
  padding: COMMON_STYLES.padding,
  borderRadius: COMMON_STYLES.borderRadius,
  textAlign: 'center',
  backgroundColor: 'rgba(156, 163, 175, 0.1)',
  border: '1px solid rgba(156, 163, 175, 0.3)',
}));

// Error message
export const OfflineEventsErrorMessage = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
}));

// Empty state
export const OfflineEventsEmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '60px 20px',
  color: FORD_COLORS.textMuted,
  '& .MuiTypography-h6': {
    color: FORD_COLORS.textLight,
    marginBottom: '8px',
  },
  '& .MuiTypography-body2': {
    color: FORD_COLORS.textMuted,
  },
}));

// Pagination container
export const OfflineEventsPagination = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `${COMMON_STYLES.padding} 0`,
  borderTop: COMMON_STYLES.border,
  marginTop: COMMON_STYLES.padding,
  '& button': {
    ...BUTTON_BASE_STYLES,
    color: FORD_COLORS.textMuted,
    minWidth: '36px',
    '&.active': {
      backgroundColor: '#4f7cff',
      color: '#ffffff',
      borderColor: '#4f7cff',
      '&:hover': {
        backgroundColor: '#3d63cc',
      },
    },
  },
}));

// Pagination text
export const OfflineEventsPaginationText = styled(Typography)(({ theme }) => ({
  color: FORD_COLORS.textMuted,
  fontSize: COMMON_STYLES.fontSize,
}));

// Primary button styling
export const OutagePrimaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#066fef',
  color: '#ffffff',
  textTransform: 'none',
  borderRadius: COMMON_STYLES.borderRadius,
  padding: '3px 16px',
  fontWeight: '500',
  transition: COMMON_STYLES.transition,
  '&:hover': {
    backgroundColor: '#0554c7',
  },
  '&:disabled': {
    backgroundColor: '#233058',
    color: '#9aa3c7',
  },
}));

// Secondary button styling - for close buttons, etc.
export const OutageSecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: FORD_COLORS.textLight,
  border: COMMON_STYLES.border,
  borderRadius: COMMON_STYLES.borderRadiusSmall,
  padding: '8px 16px',
  textTransform: 'none',
  fontWeight: 500,
  transition: COMMON_STYLES.transition,
  '&:hover': {
    backgroundColor: COMMON_STYLES.hoverBackground,
    borderColor: COMMON_STYLES.hoverBorderColor,
    transform: 'translateY(-1px)',
    color: FORD_COLORS.textLight,
  },
  '&:disabled': {
    backgroundColor: 'rgba(30,45,90,0.1)',
    color: '#6b7280',
    borderColor: '#374151',
    cursor: 'not-allowed',
    transform: 'none',
  },
}));

// Panel title text
export const OutageEventsTableTitle = styled('span')(({ theme }) => ({
  color: FORD_COLORS.textMuted, // Match Dashboard --muted
  fontSize: '13px',
  fontWeight: 500,
  letterSpacing: '0.5px',
  display: 'block',
}));